import React, { useState, useCallback } from 'react';
import {
  ChevronLeft, Upload, FileText, Download, ArrowRight,
  CheckCircle, AlertCircle, Loader2, X, RefreshCw,
  Image, Box, FileCode, Layers, ArrowRightLeft
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const categoryConfig = {
  images: {
    icon: Image,
    label: 'Images & PDFs',
    color: 'text-emerald-400',
    bg: 'bg-emerald-500/15',
    border: 'border-emerald-500/30',
    formats: ['.tiff', '.tif', '.bmp', '.webp', '.heic', '.heif', '.svg', '.gif', '.png', '.jpg', '.jpeg', '.pdf'],
    targetLabel: 'PNG / JPG',
    description: 'Convert images and PDFs for AI blueprint analysis'
  },
  '3d_mesh': {
    icon: Box,
    label: '3D Meshes',
    color: 'text-cyan-400',
    bg: 'bg-cyan-500/15',
    border: 'border-cyan-500/30',
    formats: ['.obj', '.ply', '.3mf', '.off', '.stl'],
    targetLabel: 'STL / DXF',
    description: 'Convert 3D meshes between formats'
  },
  cad: {
    icon: Layers,
    label: 'CAD Files',
    color: 'text-purple-400',
    bg: 'bg-purple-500/15',
    border: 'border-purple-500/30',
    formats: ['.igs', '.iges', '.step', '.stp'],
    targetLabel: 'STEP / DXF',
    description: 'Convert CAD files for geometry extraction'
  },
  drawings: {
    icon: FileCode,
    label: '2D Drawings',
    color: 'text-amber-400',
    bg: 'bg-amber-500/15',
    border: 'border-amber-500/30',
    formats: ['.dwg', '.dxf'],
    targetLabel: 'DXF / PNG',
    description: 'Convert 2D drawings between formats'
  }
};

// Maps source extension to available targets
const conversionTargets = {
  '.tiff': ['png', 'jpg'], '.tif': ['png', 'jpg'],
  '.bmp': ['png', 'jpg'], '.webp': ['png', 'jpg'],
  '.heic': ['png', 'jpg'], '.heif': ['png', 'jpg'],
  '.svg': ['png', 'jpg'], '.gif': ['png', 'jpg'],
  '.png': ['jpg'], '.jpg': ['png'], '.jpeg': ['png'],
  '.pdf': ['png', 'jpg'],
  '.obj': ['stl'], '.ply': ['stl'], '.3mf': ['stl'], '.off': ['stl'],
  '.stl': ['dxf'],
  '.igs': ['step'], '.iges': ['step'],
  '.step': ['dxf'], '.stp': ['dxf'],
  '.dwg': ['dxf'],
  '.dxf': ['png'],
};

const allSupportedExts = Object.keys(conversionTargets);

const FileConverterPage = () => {
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [targetFormat, setTargetFormat] = useState(null);
  const [converting, setConverting] = useState(false);
  const [result, setResult] = useState(null); // { blob, filename, size }
  const [error, setError] = useState(null);
  const [availableTargets, setAvailableTargets] = useState([]);

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') setDragActive(true);
    else if (e.type === 'dragleave') setDragActive(false);
  }, []);

  const handleFile = useCallback((f) => {
    const ext = f.name.toLowerCase().substring(f.name.lastIndexOf('.'));
    if (!allSupportedExts.includes(ext)) {
      setError(`Unsupported format: ${ext}. Supported: ${allSupportedExts.join(', ')}`);
      return;
    }
    setFile(f);
    setError(null);
    setResult(null);
    const targets = conversionTargets[ext] || [];
    setAvailableTargets(targets);
    setTargetFormat(targets[0] || null);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files?.[0]) handleFile(e.dataTransfer.files[0]);
  }, [handleFile]);

  const getFileCategory = (filename) => {
    const ext = filename?.toLowerCase().substring(filename.lastIndexOf('.'));
    for (const [key, config] of Object.entries(categoryConfig)) {
      if (config.formats.includes(ext)) return key;
    }
    return null;
  };

  const convertFile = async () => {
    if (!file || !targetFormat) return;
    setConverting(true);
    setError(null);
    setResult(null);

    const formData = new FormData();
    formData.append('file', file);
    formData.append('target_format', targetFormat);

    try {
      const response = await fetch(`${API_URL}/api/convert`, {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.detail || `Conversion failed (${response.status})`);
      }

      const blob = await response.blob();
      const disposition = response.headers.get('Content-Disposition') || '';
      const filenameMatch = disposition.match(/filename="?([^";\n]+)"?/);
      const outputFilename = filenameMatch ? filenameMatch[1] : `converted.${targetFormat}`;

      setResult({ blob, filename: outputFilename, size: blob.size });
    } catch (err) {
      setError(err.message || 'Conversion failed');
    } finally {
      setConverting(false);
    }
  };

  const downloadResult = () => {
    if (!result) return;
    const url = URL.createObjectURL(result.blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = result.filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const sendToImport = () => {
    if (!result) return;
    // Store converted file in sessionStorage as base64 for CAD Import page
    const reader = new FileReader();
    reader.onload = () => {
      sessionStorage.setItem('converted_file', JSON.stringify({
        filename: result.filename,
        data: reader.result,
        format: targetFormat
      }));
      navigate('/cad-import');
    };
    reader.readAsDataURL(result.blob);
  };

  const reset = () => {
    setFile(null);
    setResult(null);
    setError(null);
    setTargetFormat(null);
    setAvailableTargets([]);
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1048576).toFixed(1)} MB`;
  };

  const category = file ? getFileCategory(file.name) : null;
  const catConfig = category ? categoryConfig[category] : null;
  const CatIcon = catConfig?.icon || FileText;

  return (
    <div className="min-h-screen bg-industrial-bg pb-20">
      {/* Header */}
      <div className="sticky top-14 z-20 bg-industrial-surface border-b border-industrial-border px-4 py-3">
        <div className="flex items-center justify-between">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-1 text-industrial-text-secondary hover:text-industrial-text transition-colors"
            data-testid="back-btn"
          >
            <ChevronLeft className="w-5 h-5" />
            <span className="text-sm">Back</span>
          </button>
          <h2 className="font-heading font-bold text-teal-400 flex items-center gap-2">
            <ArrowRightLeft className="w-5 h-5" />
            FILE CONVERTER
          </h2>
          <div className="w-16" />
        </div>
      </div>

      <div className="p-4 space-y-4">
        {/* Supported Formats Guide */}
        {!file && !result && (
          <div className="space-y-3" data-testid="format-guide">
            <p className="text-sm text-industrial-text-secondary">
              Convert your files into formats compatible with G-Code Pro.
            </p>
            {Object.entries(categoryConfig).map(([key, config]) => {
              const Icon = config.icon;
              return (
                <div key={key} className={`${config.bg} border ${config.border} rounded-lg p-3`}>
                  <div className="flex items-center gap-2 mb-1">
                    <Icon className={`w-4 h-4 ${config.color}`} />
                    <span className={`text-sm font-medium ${config.color}`}>{config.label}</span>
                    <ArrowRight className="w-3 h-3 text-industrial-text-secondary" />
                    <span className="text-sm text-industrial-text">{config.targetLabel}</span>
                  </div>
                  <p className="text-xs text-industrial-text-secondary ml-6">{config.description}</p>
                  <div className="flex flex-wrap gap-1.5 mt-2 ml-6">
                    {config.formats.map(fmt => (
                      <span key={fmt} className="text-xs px-1.5 py-0.5 bg-black/20 rounded text-industrial-text-secondary font-mono">
                        {fmt}
                      </span>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Upload Area */}
        {!result && (
          <div
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            className={`border-2 border-dashed rounded-xl p-8 text-center transition-all cursor-pointer ${
              dragActive
                ? 'border-teal-400 bg-teal-500/10'
                : file
                ? `${catConfig?.border || 'border-industrial-border'} ${catConfig?.bg || 'bg-industrial-surface'}`
                : 'border-industrial-border hover:border-teal-400/50 bg-industrial-surface'
            }`}
            onClick={() => !file && document.getElementById('converter-file-input')?.click()}
            data-testid="upload-area"
          >
            <input
              id="converter-file-input"
              type="file"
              className="hidden"
              accept={allSupportedExts.join(',')}
              onChange={(e) => e.target.files?.[0] && handleFile(e.target.files[0])}
              data-testid="file-input"
            />
            {file ? (
              <div className="space-y-3">
                <CatIcon className={`w-10 h-10 mx-auto ${catConfig?.color || 'text-industrial-text-secondary'}`} />
                <div>
                  <p className="text-industrial-text font-medium">{file.name}</p>
                  <p className="text-xs text-industrial-text-secondary mt-1">{formatSize(file.size)}</p>
                </div>
                <button
                  onClick={(e) => { e.stopPropagation(); reset(); }}
                  className="text-xs text-red-400 hover:text-red-300 transition-colors"
                  data-testid="remove-file-btn"
                >
                  Remove file
                </button>
              </div>
            ) : (
              <div className="space-y-2">
                <Upload className="w-10 h-10 mx-auto text-industrial-text-secondary" />
                <p className="text-industrial-text">Drop file here or tap to browse</p>
                <p className="text-xs text-industrial-text-secondary">
                  Supports: PNG, JPG, PDF, STL, STEP, STP, OBJ, PLY, 3MF, DXF, DWG, IGES, SVG, TIFF, BMP, WEBP, HEIC
                </p>
              </div>
            )}
          </div>
        )}

        {/* Target Format Selection */}
        {file && !result && availableTargets.length > 0 && (
          <div className="bg-industrial-surface border border-industrial-border rounded-lg p-4" data-testid="target-selection">
            <p className="text-sm text-industrial-text-secondary mb-3">Convert to:</p>
            <div className="flex gap-2">
              {availableTargets.map(fmt => (
                <button
                  key={fmt}
                  onClick={() => setTargetFormat(fmt)}
                  className={`flex-1 py-3 rounded-lg text-sm font-medium border transition-all ${
                    targetFormat === fmt
                      ? 'bg-teal-500/20 border-teal-400 text-teal-400'
                      : 'bg-industrial-bg border-industrial-border text-industrial-text-secondary hover:border-teal-400/50'
                  }`}
                  data-testid={`target-${fmt}`}
                >
                  <span className="block text-lg font-bold">.{fmt.toUpperCase()}</span>
                  <span className="block text-xs mt-0.5 opacity-70">
                    {fmt === 'png' ? 'Lossless' : fmt === 'jpg' ? 'Compressed' : fmt === 'stl' ? '3D Mesh' : fmt === 'step' ? 'CAD' : 'Drawing'}
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Convert Button */}
        {file && !result && targetFormat && (
          <button
            onClick={convertFile}
            disabled={converting}
            className="w-full py-4 bg-teal-500 hover:bg-teal-600 disabled:bg-teal-500/50 text-white rounded-xl font-bold text-sm flex items-center justify-center gap-2 transition-colors"
            data-testid="convert-btn"
          >
            {converting ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Converting...
              </>
            ) : (
              <>
                <RefreshCw className="w-5 h-5" />
                CONVERT TO {targetFormat.toUpperCase()}
              </>
            )}
          </button>
        )}

        {/* Error */}
        {error && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 flex items-start gap-3" data-testid="error-message">
            <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-red-400 font-medium">Conversion Failed</p>
              <p className="text-xs text-red-400/80 mt-1">{error}</p>
            </div>
            <button onClick={() => setError(null)} className="ml-auto">
              <X className="w-4 h-4 text-red-400" />
            </button>
          </div>
        )}

        {/* Result */}
        {result && (
          <div className="space-y-4" data-testid="conversion-result">
            <div className="bg-teal-500/10 border border-teal-500/30 rounded-xl p-6 text-center space-y-3">
              <CheckCircle className="w-12 h-12 mx-auto text-teal-400" />
              <div>
                <p className="text-lg font-bold text-industrial-text">Conversion Complete</p>
                <p className="text-sm text-industrial-text-secondary mt-1">
                  {file?.name} <ArrowRight className="w-3 h-3 inline mx-1" /> {result.filename}
                </p>
                <p className="text-xs text-industrial-text-secondary mt-1">{formatSize(result.size)}</p>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={downloadResult}
                className="py-3 bg-industrial-surface border border-industrial-border rounded-lg text-industrial-text font-medium flex items-center justify-center gap-2 hover:border-teal-400 transition-colors"
                data-testid="download-converted-btn"
              >
                <Download className="w-4 h-4" />
                Download
              </button>
              <button
                onClick={sendToImport}
                className="py-3 bg-teal-500 text-white rounded-lg font-medium flex items-center justify-center gap-2 hover:bg-teal-600 transition-colors"
                data-testid="send-to-import-btn"
              >
                <ArrowRight className="w-4 h-4" />
                Open in CAD Import
              </button>
            </div>

            {/* Convert Another */}
            <button
              onClick={reset}
              className="w-full py-3 bg-industrial-surface border border-industrial-border rounded-lg text-industrial-text-secondary text-sm hover:border-industrial-safety transition-colors"
              data-testid="convert-another-btn"
            >
              Convert Another File
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default FileConverterPage;
